import useReveal from "@/hooks/useReveal";
import { Check, ShieldCheck, CreditCard, XCircle, RefreshCw } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
"@/components/ui/accordion";

const plans = [
{
  id: "solo",
  name: "Solo",
  audience: "For a single treating practitioner",
  price: "59",
  period: "/month",
  features: [
  "Appointments, patients and invoicing",
  "AI documentation included with fair usage",
  "1 treating practitioner",
  "Patient forms and secure document links",
  "Email support"],

  cta: "Start free trial",
  ctaLink: "/app/signup",
  highlighted: false
},
{
  id: "team",
  name: "Team",
  audience: "For small teams of up to 3 practitioners",
  price: "149",
  period: "/month",
  features: [
  "Everything in Solo",
  "Up to 3 treating practitioners",
  "AI documentation included with fair usage",
  "Online booking",
  "Priority email support"],

  cta: "Start free trial",
  ctaLink: "/app/signup",
  highlighted: true
},
{
  id: "clinic",
  name: "Clinic",
  audience: "For established clinics of up to 6 practitioners",
  price: "249",
  period: "/month",
  features: [
  "Everything in Team",
  "Up to 6 treating practitioners",
  "AI documentation included with fair usage",
  "Clinic reporting",
  "Letter templates and branded emails"],

  cta: "Start free trial",
  ctaLink: "/app/signup",
  highlighted: false
},
{
  id: "growth",
  name: "Growth",
  audience: "For growing clinics of up to 12 practitioners",
  price: "399",
  period: "/month",
  features: [
  "Everything in Clinic",
  "Up to 12 treating practitioners",
  "Higher fair-usage limits",
  "Onboarding session",
  "Priority support"],

  cta: "Start free trial",
  ctaLink: "/app/signup",
  highlighted: false
},
{
  id: "enterprise",
  name: "Enterprise",
  audience: "For clinic groups and multi-site organisations",
  price: "Custom",
  period: "",
  features: [
  "Everything in Growth",
  "12+ practitioners and multi-site",
  "Tailored onboarding",
  "Custom workflows",
  "Dedicated support"],

  cta: "Contact us",
  ctaLink: "mailto:support@lumeroflow.co.uk?subject=Enterprise%20plan%20enquiry",
  highlighted: false
}];


const reassurance = [
{ icon: CreditCard, text: "No hidden fees" },
{ icon: XCircle, text: "Cancel anytime" },
{ icon: ShieldCheck, text: "Secure setup" },
{ icon: RefreshCw, text: "Clinician-controlled review workflow" }];


const pricingFaqs = [
{
  q: "Is pricing per clinic or per practitioner?",
  a: "Pricing is per clinic, not per individual practitioner. Each plan includes a set number of practitioner seats, so your whole team can use LumeroFlow under one subscription."
},
{
  q: "Can I add more practitioners later?",
  a: "Yes. You can upgrade your plan or add additional practitioner seats at any time as your clinic grows. No disruption to your existing setup."
},
{
  q: "Do clinicians approve notes before saving?",
  a: "Always. AI-generated notes are presented as drafts for the clinician to review, edit, and approve before they are saved to the patient record. Full clinical control is maintained."
},
{
  q: "Is onboarding included?",
  a: "Solo, Team and Clinic plans include self-serve onboarding with documentation and email support. Growth includes an onboarding session, and Enterprise includes tailored onboarding with dedicated support."
},
{
  q: "Can I start small and upgrade later?",
  a: "Absolutely. Most clinics start on Solo or Team and upgrade as their team grows. Your data and setup carry over seamlessly."
}];


const PricingSection = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section
      ref={ref}
      id="pricing"
      data-testid="pricing-section"
      className="py-20 md:py-32"
      style={{ backgroundColor: "#FAF9F6" }}>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className={`text-center mb-16 reveal-section ${isVisible ? "visible" : ""}`}>
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-[#205B5A] mb-5">
            Pricing
          </p>
          <h2
            data-testid="pricing-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-[#1A1B1A] max-w-2xl mx-auto"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>

            Simple pricing for modern private clinics
          </h2>
          <p className="text-base md:text-lg text-[#4A4B4A] leading-relaxed max-w-xl mx-auto mt-5">
            Choose the plan that fits your clinic today, with room to grow as your team and workflow expand.
            AI documentation is included with fair usage on every plan.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-10">
          {plans.map((plan, i) =>
          <div
            key={plan.id}
            data-testid={`pricing-card-${plan.id}`}
            className={`relative rounded-3xl p-6 lg:p-7 flex flex-col reveal-section ${isVisible ? "visible" : ""} ${
            plan.highlighted ?
            "bg-[#131E1E] text-white border border-white/[0.06] shadow-xl" :
            "bg-white border border-[#1A1B1A]/[0.06] shadow-sm"}`
            }
            style={{ transitionDelay: `${i * 0.12}s` }}>

              {plan.highlighted &&
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-[#205B5A] text-white text-[11px] font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full">
                    Most popular
                  </span>
                </div>
            }

              <div className="mb-6">
                <h3
                className={`text-lg font-semibold mb-1.5 ${plan.highlighted ? "text-white" : "text-[#1A1B1A]"}`}
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>

                  {plan.name}
                </h3>
                <p className={`text-sm ${plan.highlighted ? "text-white/50" : "text-[#757675]"}`}>
                  {plan.audience}
                </p>
              </div>

              <div className="mb-8">
                {plan.price === "Custom" ?
              <span
                className={`text-2xl lg:text-3xl font-medium tracking-tight ${plan.highlighted ? "text-white" : "text-[#1A1B1A]"}`}
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>

                    Custom pricing
                  </span> :

              <div className="flex items-baseline gap-1">
                    <span className={`text-sm ${plan.highlighted ? "text-white/50" : "text-[#757675]"}`}>£</span>
                    <span
                  className={`text-3xl lg:text-4xl font-medium tracking-tight ${plan.highlighted ? "text-white" : "text-[#1A1B1A]"}`}
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>

                      {plan.price}
                    </span>
                    <span className={`text-sm ${plan.highlighted ? "text-white/40" : "text-[#757675]"}`}>
                      {plan.period}
                    </span>
                  </div>
              }
              </div>

              <ul className="space-y-2.5 mb-8 flex-grow">
                {plan.features.map((feature, fi) =>
              <li key={fi} className="flex items-start gap-3">
                    <Check
                  className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlighted ? "text-[#7EBDBC]" : "text-[#205B5A]"}`}
                  strokeWidth={2} />

                    <span className={`text-sm leading-relaxed ${plan.highlighted ? "text-white/70" : "text-[#4A4B4A]"}`}>
                      {feature}
                    </span>
                  </li>
              )}
              </ul>

              <a
              href={plan.ctaLink}
              data-testid={`pricing-cta-${plan.id}`}
              className={`text-center px-5 py-3 rounded-full font-medium text-sm transition-all duration-300 block ${
              plan.highlighted ?
              "bg-white text-[#131E1E] hover:bg-white/90 shadow-sm" :
              "bg-[#205B5A] text-white hover:bg-[#174544] shadow-sm"}`
              }>

                {plan.cta}
              </a>
            </div>
          )}
        </div>

        {/* Non-AI note */}
        <p className="text-center text-sm text-[#757675] mb-14" data-testid="pricing-non-ai-note">
          Need a non-AI clinic plan?{" "}
          <a href="mailto:support@lumeroflow.co.uk?subject=Non-AI%20clinic%20plan"
            className="text-[#205B5A] hover:underline">Contact us</a>.
        </p>

        {/* Reassurance strip */}
        <div className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-4 mb-20 reveal-section ${isVisible ? "visible" : ""}`} style={{ transitionDelay: "0.3s" }}>
          {reassurance.map((item, i) =>
          <div key={i} data-testid={`reassurance-${i}`} className="flex items-center gap-2.5">
              <item.icon className="w-4 h-4 text-[#205B5A]" strokeWidth={1.5} />
              <span className="text-sm text-[#4A4B4A] font-medium">{item.text}</span>
            </div>
          )}
        </div>

        {/* Pricing FAQ */}
        <div className={`max-w-3xl mx-auto reveal-section ${isVisible ? "visible" : ""}`} style={{ transitionDelay: "0.4s" }}>
          <h3
            data-testid="pricing-faq-heading"
            className="text-2xl md:text-3xl font-medium tracking-tight text-[#1A1B1A] text-center mb-10"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>

            Pricing questions
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {pricingFaqs.map((faq, i) =>
            <AccordionItem
              key={i}
              value={`pricing-faq-${i}`}
              className="border-b border-[#1A1B1A]/[0.08]"
              data-testid={`pricing-faq-item-${i}`}>

                <AccordionTrigger
                className="py-6 text-left text-base md:text-lg font-medium text-[#1A1B1A] hover:no-underline hover:text-[#205B5A] transition-colors"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid={`pricing-faq-trigger-${i}`}>

                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#4A4B4A] text-sm md:text-base leading-relaxed pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </div>
    </section>);

};

export default PricingSection;